// frontend/src/pages/BotSettingsPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { getBotSettings, updateBotSetting } from '../services/api';

// Define which settings are editable, their labels, and input types for the UI
// This config helps in rendering the form dynamically.
// Add descriptions for tooltips or helper text if desired.
const editableSettingsConfig = {
    'CAPITAL_PER_TRADE': { label: 'Capital Per Trade (₹)', type: 'number', step: '100', min: 0 },
    'SL_PERCENTAGE': { label: 'Stop Loss (%)', type: 'number', step: '0.1', min: 0, max: 100 },
    'TP_PERCENTAGE': { label: 'Take Profit (%)', type: 'number', step: '0.1', min: 0 },
    'NIFTY_LOT_SIZE': { label: 'Nifty Lot Size (Guideline)', type: 'number', step: '1', min: 1 },
    'EOD_SQUAREOFF_TIME': { label: 'EOD Square-off Time (HH:MM:SS)', type: 'text', pattern: '^([01]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$' },
    'CONSECUTIVE_LOSS_TRADE_LIMIT': { label: 'Consecutive Loss Trade Limit', type: 'number', step: '1', min: 0 },
    // Example for a boolean setting (controlled by checkbox)
    // 'SOME_BOOLEAN_SETTING': { label: 'Enable Feature X', type: 'boolean'},
    // Note: IS_BOT_ACTIVE is usually controlled via Start/Stop buttons, not a direct setting here.
};


function BotSettingsPage() {
    const [settings, setSettings] = useState({}); // Holds current values for input fields
    const [initialSettings, setInitialSettings] = useState({}); // To compare for changes
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessages, setErrorMessages] = useState({}); // Per-field errors
    const [successMessages, setSuccessMessages] = useState({}); // Per-field success

    const fetchSettings = useCallback(async () => {
        setIsLoading(true);
        setErrorMessages({});
        setSuccessMessages({});
        try {
            const response = await getBotSettings();
            // Filter response.data to only include keys present in editableSettingsConfig
            const relevantSettings = {};
            Object.keys(editableSettingsConfig).forEach(key => {
                if (response.data.hasOwnProperty(key)) {
                    relevantSettings[key] = response.data[key];
                } else {
                    // Initialize with a default if not present, or handle as needed
                    const config = editableSettingsConfig[key];
                    relevantSettings[key] = config.type === 'number' ? 0 : (config.type === 'boolean' ? false : '');
                    console.warn(`Setting ${key} not found in backend response, using default.`);
                }
            });
            setSettings(relevantSettings);
            setInitialSettings(relevantSettings);
        } catch (error) {
            console.error("Error fetching bot settings:", error);
            setErrorMessages(prev => ({ ...prev, global: error.response?.data?.error || 'Could not fetch bot settings.' }));
            setSettings({});
            setInitialSettings({});
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const handleInputChange = (key, value) => {
        const config = editableSettingsConfig[key];
        let processedValue = value;

        if (config.type === 'boolean') {
            processedValue = value; // Directly use boolean from checkbox
        } else if (config.type === 'number') {
            // Allow empty string for easier editing, but it will be parsed as NaN or 0 later
            processedValue = value === '' ? '' : value;
        }
        
        setSettings(prev => ({ ...prev, [key]: processedValue }));
        // Clear previous messages for this field
        setSuccessMessages(prev => ({ ...prev, [key]: '' }));
        setErrorMessages(prev => ({ ...prev, [key]: '' }));
    };

    const validateField = (key, value) => {
        const config = editableSettingsConfig[key];
        if (!config) return true; // No validation if not configured

        if (config.type === 'number') {
            if (value === '' || isNaN(Number(value))) {
                setErrorMessages(prev => ({ ...prev, [key]: `${config.label} must be a valid number.`}));
                return false;
            }
            const numValue = Number(value);
            if (config.min !== undefined && numValue < config.min) {
                setErrorMessages(prev => ({ ...prev, [key]: `${config.label} must be at least ${config.min}.`}));
                return false;
            }
            if (config.max !== undefined && numValue > config.max) {
                setErrorMessages(prev => ({ ...prev, [key]: `${config.label} must be at most ${config.max}.`}));
                return false;
            }
        } else if (config.type === 'text' && config.pattern) {
            if (!new RegExp(config.pattern).test(value)) {
                setErrorMessages(prev => ({ ...prev, [key]: `Invalid format for ${config.label}. Example: 15:15:00`}));
                return false;
            }
        }
        return true;
    };

    const handleSaveSetting = async (key) => {
        const value = settings[key];
        const config = editableSettingsConfig[key];

        if (!validateField(key, value)) {
            return; // Stop if validation fails
        }
        
        // Convert to number if type is number before sending
        let valueToSend = value;
        if (config.type === 'number') {
            valueToSend = Number(value);
        }

        try {
            setIsLoading(true); // Indicate loading during save
            await updateBotSetting(key, valueToSend);
            setSuccessMessages(prev => ({ ...prev, [key]: `Setting '${config.label || key}' updated!`}));
            setInitialSettings(prev => ({ ...prev, [key]: value })); // Update initial settings to reflect saved state
        } catch (error) {
            console.error(`Error updating setting ${key}:`, error);
            setErrorMessages(prev => ({ ...prev, [key]: error.response?.data?.error || `Could not update '${config.label || key}'.`}));
            // Optionally revert to initial value on error if desired, or let user correct
            // setSettings(prev => ({ ...prev, [key]: initialSettings[key] }));
        } finally {
            setIsLoading(false);
        }
    };
    
    const displayableSettingKeys = Object.keys(editableSettingsConfig).sort();

    if (isLoading && !Object.keys(settings).length) { // Show loading only on initial fetch
        return <p>Loading settings...</p>;
    }
    
    if (errorMessages.global && !Object.keys(settings).length) {
        return <p style={{ color: 'red' }}><strong>Error: {errorMessages.global}</strong></p>;
    }

    return (
        <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif', maxWidth: '700px', margin: 'auto' }}>
            <h2>Bot Settings</h2>
            <form onSubmit={(e) => e.preventDefault()}>
                {displayableSettingKeys.map(key => {
                    const config = editableSettingsConfig[key];
                    const currentValue = settings[key] === null || settings[key] === undefined ? 
                                         (config.type === 'number' ? '' : (config.type === 'boolean' ? false : '')) 
                                         : settings[key];
                    const isModified = JSON.stringify(currentValue) !== JSON.stringify(initialSettings[key]);

                    return (
                        <div key={key} style={{ marginBottom: '20px', border: '1px solid #eee', padding: '15px', borderRadius: '5px' }}>
                            <label htmlFor={key} style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '1.1em' }}>
                                {config.label || key}
                            </label>
                            {config.type === 'boolean' ? (
                                <input
                                    type="checkbox"
                                    id={key}
                                    checked={!!currentValue} // Ensure it's boolean
                                    onChange={(e) => handleInputChange(key, e.target.checked)}
                                    style={{ marginRight: '10px' }}
                                />
                            ) : (
                                <input
                                    type={config.type || 'text'}
                                    id={key}
                                    value={currentValue}
                                    onChange={(e) => handleInputChange(key, e.target.value)}
                                    step={config.step || undefined}
                                    min={config.min !== undefined ? config.min : undefined}
                                    max={config.max !== undefined ? config.max : undefined}
                                    pattern={config.pattern || undefined}
                                    placeholder={config.label || key}
                                    style={{ width: 'calc(70% - 10px)', padding: '10px', marginRight: '10px', border: '1px solid #ccc', borderRadius: '4px' }}
                                />
                            )}
                            <button 
                                type="button" 
                                onClick={() => handleSaveSetting(key)}
                                disabled={!isModified || isLoading} // Disable if no change or if any save is in progress
                                style={{ padding: '10px 18px', background: isModified ? '#007bff' : '#ccc', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                            >
                                {isLoading && initialSettings[key] !== currentValue ? 'Saving...' : 'Save'}
                            </button>
                            {isModified && !successMessages[key] && !errorMessages[key] && <span style={{marginLeft: '10px', fontSize: '0.9em', color: 'orange'}}> (modified)</span>}
                            {successMessages[key] && <span style={{ marginLeft: '10px', color: 'green' }}>{successMessages[key]}</span>}
                            {errorMessages[key] && <span style={{ marginLeft: '10px', color: 'red' }}>{errorMessages[key]}</span>}
                        </div>
                    );
                })}
            </form>
            <button onClick={fetchSettings} disabled={isLoading} style={{marginTop: '20px', padding: '10px 15px'}}>
                {isLoading ? 'Reloading...' : 'Reload All Settings from Server'}
            </button>
        </div>
    );
}

export default BotSettingsPage;